import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMediaQuery } from "@genie-ui/react";
import { Constants } from "@constants/Constants";
import logout from "@utils/Logout";
import { updateStateSubject } from "@gartner/genie-messenger-util";
import * as FullStory from "@fullstory/browser";
import { ACTIS_REPORTING } from "@utils/actisReporting";
import { getGCOMEnv, getCurrentEnvForAppLauncher } from "@utils/utilities";
import {
  Header as GravityHeader,
  LeftNavigationComponent as LeftNavigation,
} from "@gravity/tools";

import "./header.scss";

const Header = ({ currentUser }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [showNavigation, setShowNavigation] = useState(false);
  const isMobile = useMediaQuery("(max-width:767px)");
  const { roles } = currentUser || {};

  const getPage = () => {
    const mapping = {
      [Constants.ROUTES.BASE_URL]: Constants.PAGE_NAME.POPULAR_INSIGHTS_PAGE,
      [Constants.ROUTES.DETAILS]: Constants.PAGE_NAME.USE_CASE_DETAILS_PAGE,
    };
    return mapping[window.location.pathname] || "";
  };

  const handleAppLogoClick = () => {
    window.open(getGCOMEnv(), "_blank");
    const page = getPage();
    ACTIS_REPORTING.GARTNER_LOGO.push({
      action: Constants.ACTIS.GARTNER_LOGO.ACTION.replace("##PAGE##", page),
      label: Constants.ACTIS.GARTNER_LOGO.LABEL,
    });
  };

  const handleSecondaryLogoClick = () => {
    const page = getPage();
    ACTIS_REPORTING.INSIGHTS_PLATFORM.push({
      action: Constants.ACTIS.INSIGHTS_LOGO.ACTION.replace("##PAGE##", page),
      label: Constants.ACTIS.INSIGHTS_LOGO.LABEL,
    });
    updateStateSubject("secondaryLogoClick", true);
    navigate(Constants.ROUTES.USE_CASES);
  };

  const handleHelpClick = () => {
    window.open(Constants.HELP.URL, "_blank");
    ACTIS_REPORTING.USER_ACTION.push({
      action: Constants.ACTIS.INTERCOM_HELP.ACTION,
      label: Constants.ACTIS.INTERCOM_HELP.LABEL,
    });
  };

  const onItemClick = (e, item) => {
    switch (item) {
      case "logo":
        handleAppLogoClick();
        break;
      case "appTitle":
        handleSecondaryLogoClick();
        break;
      case "help":
        handleHelpClick();
        break;
      default:
        break;
    }
  };

  const handleLogout = () => {
    updateStateSubject("logout", {});
    if (logout) {
      logout();
    }
  };

  const onLeftNavItemClick = (tab) => () => {
    updateStateSubject("leftNavigationItemClick", tab);
  };
  const setPathNameForFullStory = () => {
    let currentPath = pathname;
    if (pathname === "/") {
      currentPath = Constants.FULL_STORY.HOME_PATH;
    } else if (pathname.includes("dashboard")) {
      currentPath = Constants.FULL_STORY.DASHBOARD_PATH;
    }
    FullStory.setVars("page", {
      pageName: currentPath,
    });
  };

  useEffect(() => {
    setPathNameForFullStory();
    if (currentUser) {
      if (
        window.location.href.indexOf(Constants.ROUTES.ACCESS_DENIED) < 0 &&
        !Constants.ROLE.filter((element) => roles.includes(element)).length
      ) {
        navigate(Constants.ROUTES.ACCESS_DENIED);
      } else if (
        window.location.href.indexOf(Constants.ROUTES.ACCESS_DENIED) > -1 &&
        Constants.ROLE.filter((element) => roles.includes(element)).length
      ) {
        navigate(Constants.ROUTES.USE_CASES);
      }
    }
  }, [pathname]);

  useEffect(() => {
    if (currentUser?.userId) {
      const userName = `${currentUser?.encryptedFirstName}${
        currentUser?.encryptedLastName
          ? " " + currentUser?.encryptedLastName
          : ""
      }`;
      const orgId = currentUser?.orgId;
      const isGartnerUser = currentUser?.email
        ?.toString()
        .includes("@gartner.com");
      const roles = currentUser?.roles;
      let userRole = Constants.GUEST_ROLE;
      if (roles.includes(Constants.GARTNER_ASSOCIATE_ROLE)) {
        userRole = Constants.GARTNER_ASSOCIATE_ROLE;
      } else if (roles.includes(Constants.CLIENT_ROLE)) {
        userRole = Constants.CLIENT_ROLE;
      }
      FullStory.identify(currentUser?.userId, {
        displayName: userName,
        orgId: orgId,
        isGartnerUser: isGartnerUser,
        role: userRole,
      });
    }
  }, [currentUser]);

  const userMenuListOptions = [
    {
      id: "sign-out",
      label: Constants.HEADER.SIGN_OUT,
      onClick: handleLogout,
    },
  ];

  const leftNavigationPrimaryItems = [
    {
      icon: "HOME",
      id: "home",
      label: "Homepage",
      type: "INTERNAL_LINK",
      onClick: onLeftNavItemClick(Constants.TABS[0]),
    },
    {
      icon: "USE_CASE",
      id: "usecase",
      label: "Use Cases",
      type: "INTERNAL_LINK",
      onClick: onLeftNavItemClick(Constants.TABS[1]),
    },
    {
      icon: "OVERVIEW_DASHBOARD",
      id: "scorecards",
      label: "Assessments",
      type: "INTERNAL_LINK",
      onClick: onLeftNavItemClick(Constants.TABS[2]),
    },
    {
      icon: "CASE_EXAMPLE",
      id: "caseexamples",
      label: "Case Examples",
      type: "INTERNAL_LINK",
      onClick: onLeftNavItemClick(Constants.TABS[3]),
    },
  ];

  return (
    <div>
      <GravityHeader
        className="mercury-c-gravityHeader"
        appName={Constants.HEADER.SECONDARY_LOGO_LABEL}
        userFirstName={currentUser?.firstName || ""}
        userLastName={currentUser?.lastName || ""}
        userEmail={currentUser?.email || ""}
        showAppLauncher={true}
        showHelp={true}
        userMenuListOptions={userMenuListOptions}
        onItemClick={onItemClick}
        isMenuOpen={showNavigation}
        onMenuClick={(isOpen) => setShowNavigation(isOpen)}
        appLauncherProps={{
          environment: getCurrentEnvForAppLauncher(),
          preloadModules: true,
          preloadTimeout: 1500,
        }}
      />
      {isMobile && (
        <LeftNavigation
          isMobilePrimaryNavOpen={isMobile && showNavigation}
          onToggleMobilePrimaryNav={(isOpen) => setShowNavigation(isOpen)}
          primaryItems={leftNavigationPrimaryItems}
        />
      )}
    </div>
  );
};

export default Header;
